import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Pagination,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CardMembershipOutlinedIcon from "@mui/icons-material/CardMembershipOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client/react";
import {
  GET_STUDENT_BY_ID,
  GET_STUDENT_PURCHASED_PASSES,
  GET_STUDENT_OVERALL_ATTENDANCE_HISTORY,
  GET_STUDENT_EVENT_BOOKINGS,
} from "../graphql/query";
import themestyle from "../theme";
import type { GetStudentByIdResponse } from "../interfaces/student.interface";
import type { GetStudentPurchasedPassesResponse } from "../interfaces/studentPassPurchase.interface";
import type { GetStudentOverallAttendanceHistoryResponse } from "../interfaces/attendance.interface";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const PASS_TYPE_LABELS: Record<string, string> = {
  DROP_IN: "Drop In",
  WEEKLY_4_CLASSES: "Weekly 4 Classes",
  MONTHLY_4_CLASSES: "Monthly 4 Classes",
};

function formatDate(dateStr?: string | null) {
  if (!dateStr) return "—";
  const d = new Date(dateStr.includes("T") ? dateStr : `${dateStr}T00:00:00`);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function initials(name: string) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "S";
}

// ─── Shared primitives ────────────────────────────────────────────────────────

function SectionCard({ children, sx = {} }: { children: React.ReactNode; sx?: any }) {
  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #E9EEF5",
        borderRadius: "16px",
        overflow: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function SectionHeader({ title, count }: { title: string; count?: number }) {
  return (
    <Box
      sx={{
        px: 3,
        py: 2,
        borderBottom: "1px solid #F0F4F8",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      <Typography sx={{ fontWeight: 800, fontSize: 15, color: "#111827" }}>
        {title}
      </Typography>
      {count !== undefined && (
        <Chip
          label={count}
          size="small"
          sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: "#F0F4F8", color: "#6B7280" }}
        />
      )}
    </Box>
  );
}

function InfoItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        alignItems: "flex-start",
        p: 2,
        borderRadius: "12px",
        bgcolor: "#FAFBFC",
        border: "1px solid #F0F4F8",
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: "10px",
          bgcolor: "#EEF7E9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#4CAF50",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", fontSize: 10 }}>
          {label}
        </Typography>
        <Typography sx={{ fontWeight: 600, color: "#111827", fontSize: 13, mt: 0.25, wordBreak: "break-word" }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Overview ─────────────────────────────────────────────────────────────────

function OverviewSection({ student }: { student: any }) {
  const infoItems = [
    { icon: <MailOutlineIcon sx={{ fontSize: 18 }} />, label: "Email", value: student.email || "—" },
    { icon: <PhoneOutlinedIcon sx={{ fontSize: 18 }} />, label: "Phone", value: student.phoneNumber || "—" },
    { icon: <PhoneOutlinedIcon sx={{ fontSize: 18 }} />, label: "Alt. Phone", value: student.additionalPhoneNumber || "—" },
    { icon: <CakeOutlinedIcon sx={{ fontSize: 18 }} />, label: "Date of Birth", value: formatDate(student.dateOfBirth) },
    { icon: <LocationOnOutlinedIcon sx={{ fontSize: 18 }} />, label: "Address", value: student.address || "—" },
    { icon: <SchoolOutlinedIcon sx={{ fontSize: 18 }} />, label: "Assign Type", value: student.assignType || "—" },
  ];

  const assignedClasses: string[] = student.assignToClass ?? [];

  return (
    <Stack spacing={2}>
      {/* Personal Info */}
      <SectionCard>
        <SectionHeader title="Personal Information" />
        <Box sx={{ p: 2.5, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
          {infoItems.map((item) => (
            <InfoItem key={item.label} icon={item.icon} label={item.label} value={item.value} />
          ))}
        </Box>
        {assignedClasses.length > 0 && (
          <Box sx={{ px: 2.5, pb: 2.5 }}>
            <Box sx={{ p: 2, borderRadius: "12px", bgcolor: "#FAFBFC", border: "1px solid #F0F4F8", display: "flex", gap: 1.5, alignItems: "flex-start" }}>
              <Box sx={{ width: 34, height: 34, borderRadius: "10px", bgcolor: "#EEF7E9", display: "flex", alignItems: "center", justifyContent: "center", color: "#4CAF50", flexShrink: 0 }}>
                <SchoolOutlinedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", fontSize: 10, display: "block" }}>
                  Assigned Classes
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.75 }}>
                  {assignedClasses.map((cls: string) => (
                    <Chip
                      key={cls}
                      label={cls}
                      size="small"
                      sx={{ height: 24, fontSize: 12, fontWeight: 700, bgcolor: "#EEF7E9", color: "#2E7D32", border: "1px solid #C8E6C9" }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </SectionCard>

      {/* Account Details */}
      <SectionCard>
        <SectionHeader title="Account Details" />
        <Box sx={{ p: 2.5, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
          <InfoItem
            icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />}
            label="Member Since"
            value={formatDate(student.created_at)}
          />
          <InfoItem
            icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />}
            label="Last Updated"
            value={formatDate(student.updated_at)}
          />
        </Box>
      </SectionCard>
    </Stack>
  );
}

// ─── Passes ───────────────────────────────────────────────────────────────────

const PASSES_PAGE_SIZE = 6;

function PassesSection({ passes }: { passes: any[] }) {
  const [page, setPage] = useState(1);
  const pageCount = Math.ceil(passes.length / PASSES_PAGE_SIZE);
  const paged = passes.slice((page - 1) * PASSES_PAGE_SIZE, page * PASSES_PAGE_SIZE);

  if (passes.length === 0) {
    return (
      <SectionCard>
        <Box sx={{ py: 8, textAlign: "center" }}>
          <CardMembershipOutlinedIcon sx={{ fontSize: 48, color: "#D1D5DB", mb: 1.5 }} />
          <Typography sx={{ color: "#9CA3AF", fontWeight: 600 }}>No passes purchased yet</Typography>
        </Box>
      </SectionCard>
    );
  }

  return (
    <SectionCard>
      <SectionHeader title="Purchased Passes" count={passes.length} />
      <Box sx={{ p: 2.5, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
        {paged.map((p, idx) => (
          <Box
            key={p.id || idx}
            sx={{
              border: "1px solid #E9EEF5",
              borderRadius: "14px",
              overflow: "hidden",
              bgcolor: "#FAFBFC",
            }}
          >
            {/* Colored top accent */}
            <Box
              sx={{
                height: 4,
                bgcolor: p.status === "ACTIVE" ? "#4CAF50" : "#E53935",
                borderRadius: "14px 14px 0 0",
              }}
            />
            <Box sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: 14, color: "#111827" }} noWrap>
                    {p.passName}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 600 }}>
                    {PASS_TYPE_LABELS[p.passType] || p.passType} · {p.passDbId}
                  </Typography>
                </Box>
                <Chip
                  label={p.status === "ACTIVE" ? "Active" : p.status === "EXPIRED" ? "Expired" : p.status}
                  size="small"
                  sx={{
                    ml: 1,
                    flexShrink: 0,
                    height: 22,
                    fontWeight: 800,
                    fontSize: 10,
                    bgcolor: p.status === "ACTIVE" ? "#E8F5E9" : "#FEF2F2",
                    color: p.status === "ACTIVE" ? "#2E7D32" : "#B91C1C",
                  }}
                />
              </Stack>
              <Divider sx={{ my: 1.5, borderColor: "#F0F4F8" }} />
              <Stack direction="row" justifyContent="space-between">
                <Box>
                  <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 700, textTransform: "uppercase", fontSize: 10 }}>
                    Class
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#4CAF50" }}>
                    {p.className || "—"}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 700, textTransform: "uppercase", fontSize: 10 }}>
                    Booked On
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>
                    {formatDate(p.purchasedAt)}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Box>
        ))}
      </Box>
      {pageCount > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2, borderTop: "1px solid #F0F4F8" }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, v) => setPage(v)}
            size="small"
            sx={{ "& .MuiPaginationItem-root": { fontWeight: 700 }, "& .Mui-selected": { bgcolor: "#a3d65c !important", color: "#fff" } }}
          />
        </Box>
      )}
    </SectionCard>
  );
}

// ─── Attendance ───────────────────────────────────────────────────────────────

const ATTENDANCE_PAGE_SIZE = 10;

function AttendanceSection({ data }: { data: any }) {
  const [histPage, setHistPage] = useState(1);
  const history = data?.history ?? [];
  const total = data?.totalSessions ?? 0;
  const present = data?.presentCount ?? 0;
  const absent = data?.absentCount ?? 0;
  const rate = total > 0 ? Math.round((present / total) * 100) : 0;
  const histPageCount = Math.ceil(history.length / ATTENDANCE_PAGE_SIZE);
  const pagedHistory = history.slice((histPage - 1) * ATTENDANCE_PAGE_SIZE, histPage * ATTENDANCE_PAGE_SIZE);

  const stats = [
    { label: "Total Sessions", value: total, color: "#4CAF50", bg: "#F0FBF0", valueBg: "#E8F5E9" },
    { label: "Present", value: present, color: "#1C86C8", bg: "#EDF6FF", valueBg: "#DBEAFE" },
    { label: "Absent", value: absent, color: "#E53935", bg: "#FEF1F1", valueBg: "#FEE2E2" },
  ];

  return (
    <Stack spacing={2}>
      {/* Stat cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
        {stats.map((s) => (
          <Box
            key={s.label}
            sx={{
              bgcolor: s.bg,
              border: `1px solid ${s.valueBg}`,
              borderRadius: "16px",
              p: 2.5,
              textAlign: "center",
            }}
          >
            <Typography sx={{ fontSize: 40, fontWeight: 900, color: s.color, lineHeight: 1 }}>
              {s.value}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              {s.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Attendance rate */}
      {total > 0 && (
        <SectionCard>
          <Box sx={{ p: 2.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Typography sx={{ fontWeight: 700, color: "#374151", fontSize: 14 }}>
                Attendance Rate
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: rate >= 75 ? "#4CAF50" : rate >= 50 ? "#F59E0B" : "#E53935",
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: 900,
                    fontSize: 18,
                    color: rate >= 75 ? "#4CAF50" : rate >= 50 ? "#F59E0B" : "#E53935",
                  }}
                >
                  {rate}%
                </Typography>
              </Box>
            </Stack>
            <Box sx={{ height: 10, borderRadius: 999, bgcolor: "#F0F4F8", overflow: "hidden" }}>
              <Box
                sx={{
                  height: "100%",
                  width: `${rate}%`,
                  bgcolor: rate >= 75 ? "#4CAF50" : rate >= 50 ? "#F59E0B" : "#E53935",
                  borderRadius: 999,
                  transition: "width 0.6s ease",
                }}
              />
            </Box>
            <Stack direction="row" justifyContent="space-between" mt={1}>
              <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 600 }}>
                {present} of {total} sessions attended
              </Typography>
              <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 600 }}>
                {absent} missed
              </Typography>
            </Stack>
          </Box>
        </SectionCard>
      )}

      {/* History */}
      <SectionCard>
        <SectionHeader title="Attendance History" count={history.length} />
        {history.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <FactCheckOutlinedIcon sx={{ fontSize: 40, color: "#D1D5DB", mb: 1 }} />
            <Typography sx={{ color: "#9CA3AF", fontWeight: 600 }}>No records found</Typography>
          </Box>
        ) : (
          <Box>
            {/* Column headers */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1.5fr 100px",
                gap: 2,
                px: 3,
                py: 1.25,
                bgcolor: "#F8FAFC",
                borderBottom: "1px solid #F0F4F8",
              }}
            >
              {["Date", "Class", "Status"].map((h) => (
                <Typography key={h} variant="caption" sx={{ fontWeight: 800, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  {h}
                </Typography>
              ))}
            </Box>
            {pagedHistory.map((row: any, idx: number) => (
              <Box
                key={`${row.date}-${row.className}-${idx}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1.5fr 100px",
                  gap: 2,
                  px: 3,
                  py: 1.75,
                  alignItems: "center",
                  borderBottom: idx < pagedHistory.length - 1 ? "1px solid #F8FAFC" : "none",
                  "&:hover": { bgcolor: "#FAFBFC" },
                }}
              >
                <Typography sx={{ fontSize: 13, color: "#6B7280", fontWeight: 600 }}>
                  {formatDate(row.date)}
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
                  {row.className}
                </Typography>
                <Chip
                  size="small"
                  icon={
                    row.isPresent ? (
                      <CheckCircleOutlineIcon sx={{ fontSize: "13px !important" }} />
                    ) : (
                      <CancelOutlinedIcon sx={{ fontSize: "13px !important" }} />
                    )
                  }
                  label={row.isPresent ? "Present" : "Absent"}
                  sx={{
                    height: 24,
                    fontWeight: 700,
                    fontSize: 11,
                    bgcolor: row.isPresent ? "#E8F5E9" : "#FEF2F2",
                    color: row.isPresent ? "#2E7D32" : "#B91C1C",
                    "& .MuiChip-icon": { color: "inherit" },
                  }}
                />
              </Box>
            ))}
            {histPageCount > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2, borderTop: "1px solid #F0F4F8" }}>
                <Pagination
                  count={histPageCount}
                  page={histPage}
                  onChange={(_, v) => setHistPage(v)}
                  size="small"
                  sx={{ "& .MuiPaginationItem-root": { fontWeight: 700 }, "& .Mui-selected": { bgcolor: "#a3d65c !important", color: "#fff" } }}
                />
              </Box>
            )}
          </Box>
        )}
      </SectionCard>
    </Stack>
  );
}

// ─── Activity ─────────────────────────────────────────────────────────────────

const ACTIVITY_PAGE_SIZE = 10;

function ActivitySection({ passes, events = [], attendanceHistory }: { passes: any[]; events?: any[]; attendanceHistory: any[] }) {
  const [page, setPage] = useState(1);
  const totalEvents = useMemo(() => {
    const passEvents = passes.map((p) => ({
      date: p.purchasedAt,
      type: "pass" as const,
      title: `Pass purchased`,
      detail: p.passName,
      sub: p.className ? `Class: ${p.className}` : PASS_TYPE_LABELS[p.passType] || p.passType,
      status: p.status,
    }));
    const eventPurchaseEvents = events.map((e) => ({
      date: e.bookedAt,
      type: "event" as const,
      title: `Event ticket purchased`,
      detail: e.eventName,
      sub: `Ticket # ${e.id.slice(-6).toUpperCase()}`,
      status: e.status,
    }));
    const attendanceEvents = attendanceHistory.map((a) => ({
      date: `${a.date}T00:00:00`,
      type: "attendance" as const,
      title: a.isPresent ? "Attended class" : "Missed class",
      detail: a.className,
      sub: formatDate(a.date),
      status: a.isPresent ? "PRESENT" : "ABSENT",
    }));
    return [...passEvents, ...eventPurchaseEvents, ...attendanceEvents].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
  }, [passes, events, attendanceHistory]);

  const pageCount = Math.ceil(totalEvents.length / ACTIVITY_PAGE_SIZE);
  const pagedEvents = totalEvents.slice((page - 1) * ACTIVITY_PAGE_SIZE, page * ACTIVITY_PAGE_SIZE);

  if (totalEvents.length === 0) {
    return (
      <SectionCard>
        <Box sx={{ py: 8, textAlign: "center" }}>
          <ShowChartIcon sx={{ fontSize: 48, color: "#D1D5DB", mb: 1.5 }} />
          <Typography sx={{ color: "#9CA3AF", fontWeight: 600 }}>No activity recorded yet</Typography>
        </Box>
      </SectionCard>
    );
  }

  const getConfig = (type: string, status: string) => {
    if (type === "pass") return { bg: "#EBF2FF", color: "#1565C0", icon: <ConfirmationNumberOutlinedIcon sx={{ fontSize: 18 }} /> };
    if (type === "event") return { bg: "#F5F0FF", color: "#8B5CF6", icon: <ConfirmationNumberOutlinedIcon sx={{ fontSize: 18 }} /> };
    if (status === "PRESENT") return { bg: "#E8F5E9", color: "#2E7D32", icon: <CheckCircleOutlineIcon sx={{ fontSize: 18 }} /> };
    return { bg: "#FEF2F2", color: "#B91C1C", icon: <CancelOutlinedIcon sx={{ fontSize: 18 }} /> };
  };

  return (
    <SectionCard>
      <SectionHeader title="Activity Timeline" count={totalEvents.length} />
      <Box sx={{ px: 3, py: 2.5 }}>
        {pagedEvents.map((ev, idx) => {
          const cfg = getConfig(ev.type, ev.status);
          return (
            <Box key={idx} sx={{ display: "flex", gap: 2, position: "relative" }}>
              {/* Connector line */}
              {idx < pagedEvents.length - 1 && (
                <Box
                  sx={{
                    position: "absolute",
                    left: 17,
                    top: 38,
                    bottom: -10,
                    width: 2,
                    bgcolor: "#F0F4F8",
                    zIndex: 0,
                  }}
                />
              )}

              {/* Icon bubble */}
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  bgcolor: cfg.bg,
                  color: cfg.color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  zIndex: 1,
                  border: `2px solid ${cfg.color}22`,
                }}
              >
                {cfg.icon}
              </Box>

              {/* Content */}
              <Box
                sx={{
                  flex: 1,
                  pb: idx < pagedEvents.length - 1 ? 2.5 : 0,
                  pt: 0.25,
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: cfg.color, textTransform: "uppercase", fontSize: 10, letterSpacing: "0.5px" }}>
                      {ev.type === "pass" ? "Pass" : ev.type === "event" ? "Event" : "Attendance"}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: 13.5, lineHeight: 1.3 }}>
                      {ev.detail}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 600 }}>
                      {ev.sub}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}>
                    {formatDate(ev.date)}
                  </Typography>
                </Stack>
              </Box>
            </Box>
          );
        })}
      </Box>
      {pageCount > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2, borderTop: "1px solid #F0F4F8" }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, v) => setPage(v)}
            size="small"
            sx={{ "& .MuiPaginationItem-root": { fontWeight: 700 }, "& .Mui-selected": { bgcolor: "#a3d65c !important", color: "#fff" } }}
          />
        </Box>
      )}
    </SectionCard>
  );
}

// ─── Events ───────────────────────────────────────────────────────────────────

function EventsSection({ events }: { events: any[] }) {
  if (events.length === 0) {
    return (
      <SectionCard>
        <Box sx={{ py: 8, textAlign: "center" }}>
          <ConfirmationNumberOutlinedIcon sx={{ fontSize: 48, color: "#D1D5DB", mb: 1.5 }} />
          <Typography sx={{ color: "#9CA3AF", fontWeight: 600 }}>No event tickets purchased yet</Typography>
        </Box>
      </SectionCard>
    );
  }

  return (
    <SectionCard>
      <SectionHeader title="Purchased Event Tickets" count={events.length} />
      <Box sx={{ p: 2.5, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
        {events.map((ev, idx) => (
          <Box
            key={ev.id || idx}
            sx={{
              border: "1px solid #E9EEF5",
              borderRadius: "14px",
              overflow: "hidden",
              bgcolor: "#FAFBFC",
            }}
          >
            <Box
              sx={{
                height: 4,
                bgcolor: "#8B5CF6",
                borderRadius: "14px 14px 0 0",
              }}
            />
            <Box sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: 14, color: "#111827" }} noWrap>
                    {ev.eventName}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 600 }}>
                    Event Ticket · {ev.id.slice(-6).toUpperCase()}
                  </Typography>
                </Box>
                <Chip
                  label={ev.status}
                  size="small"
                  sx={{
                    ml: 1,
                    flexShrink: 0,
                    height: 22,
                    fontWeight: 800,
                    fontSize: 10,
                    bgcolor: ev.status === "CONFIRMED" ? "#E8F5E9" : "#FEF2F2",
                    color: ev.status === "CONFIRMED" ? "#2E7D32" : "#B91C1C",
                  }}
                />
              </Stack>
              <Divider sx={{ my: 1.5, borderColor: "#F0F4F8" }} />
              <Stack direction="row" justifyContent="space-between">
                <Box>
                  <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 700, textTransform: "uppercase", fontSize: 10 }}>
                    Amount
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#8B5CF6" }}>
                    ₹{ev.amount?.toLocaleString('en-IN') || "0"}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography variant="caption" sx={{ color: "#9CA3AF", fontWeight: 700, textTransform: "uppercase", fontSize: 10 }}>
                    Booked On
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>
                    {formatDate(ev.bookedAt)}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Box>
        ))}
      </Box>
    </SectionCard>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const TABS = [
  { label: "Overview", icon: <PersonOutlineIcon sx={{ fontSize: 16 }} /> },
  { label: "Passes", icon: <CardMembershipOutlinedIcon sx={{ fontSize: 16 }} /> },
  { label: "Events", icon: <ConfirmationNumberOutlinedIcon sx={{ fontSize: 16 }} /> },
  { label: "Attendance", icon: <FactCheckOutlinedIcon sx={{ fontSize: 16 }} /> },
  { label: "Activity", icon: <ShowChartIcon sx={{ fontSize: 16 }} /> },
];

const StudentReport: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { data: studentData, loading: studentLoading } =
    useQuery<GetStudentByIdResponse>(GET_STUDENT_BY_ID, {
      variables: { id },
      skip: !id,
      fetchPolicy: "network-only",
    });

  const { data: passesData, loading: passesLoading } =
    useQuery<GetStudentPurchasedPassesResponse>(GET_STUDENT_PURCHASED_PASSES, {
      variables: { studentId: id },
      skip: !id,
      fetchPolicy: "network-only",
    });

  const { data: eventsData, loading: eventsLoading } =
    useQuery(GET_STUDENT_EVENT_BOOKINGS, {
      variables: { studentId: id },
      skip: !id,
      fetchPolicy: "network-only",
    });

  const { data: attendanceData, loading: attendanceLoading } =
    useQuery<GetStudentOverallAttendanceHistoryResponse>(
      GET_STUDENT_OVERALL_ATTENDANCE_HISTORY,
      { variables: { studentId: id }, skip: !id, fetchPolicy: "network-only" },
    );

  const isLoading = studentLoading || passesLoading || attendanceLoading || eventsLoading;
  const student = studentData?.getStudentById?.data;
  const passes = passesData?.getStudentPurchasedPasses?.data ?? [];
  const eventBookings = eventsData?.getStudentEventBookings?.data ?? [];
  const attendanceSummary = attendanceData?.getStudentOverallAttendanceHistory?.data;
  const attendanceHistory = attendanceSummary?.history ?? [];

  const quickStats = useMemo(() => {
    const total = attendanceSummary?.totalSessions ?? 0;
    const present = attendanceSummary?.presentCount ?? 0;

    // Calculate total spending
    const passSpending = passes.reduce((sum: number, p: any) => sum + (p.amount || 0), 0);
    const eventSpending = eventBookings.reduce((sum: number, e: any) => sum + (e.amount || 0), 0);

    return {
      passes: passes.length,
      events: eventBookings.length,
      spending: passSpending + eventSpending,
      rate: total > 0 ? Math.round((present / total) * 100) : 0,
      classes: student?.assignToClass?.length ?? 0,
    };
  }, [passes, eventBookings, attendanceSummary, student]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress size={36} sx={{ color: "#a3d65c" }} />
      </Box>
    );
  }

  if (!student) {
    return (
      <Box sx={{ mt: 8, textAlign: "center" }}>
        <Typography sx={{ color: "#9CA3AF", fontWeight: 600 }}>Student not found.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: { xs: 1, sm: 2 }, px: { xs: 1, md: 3 }, pb: 6 }}>
      {/* Back link */}
      <Box
        onClick={() => navigate("/students")}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          color: "#6B7280",
          fontSize: 13,
          fontWeight: 600,
          mb: 2,
          "&:hover": { color: "#111827" },
        }}
      >
        <ArrowBackIosNewIcon sx={{ fontSize: 12 }} />
        Back to Students
      </Box>

      {/* ── Profile Hero ── */}
      <Box
        sx={{
          bgcolor: "#fff",
          border: "1px solid #E9EEF5",
          borderRadius: "20px",
          overflow: "hidden",
          mb: 2.5,
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        }}
      >
        {/* Green top stripe */}
        <Box sx={{ p: 3 }}>
          <Stack direction={{ xs: "column", sm: "row" }} gap={2.5} alignItems={{ sm: "center" }}>
            {/* Avatar */}
            <Avatar
              src={student.uploadPhoto ? `data:image/jpeg;base64,${student.uploadPhoto}` : undefined}
              sx={{
                width: 72,
                height: 72,
                bgcolor: "#a3d65c",
                color: "#fff",
                fontWeight: 900,
                fontSize: 26,
                flexShrink: 0,
                boxShadow: "0 4px 14px rgba(163,214,92,0.4)",
              }}
            >
              {initials(student.fullName || "")}
            </Avatar>

            {/* Name block */}
            <Box flex={1}>
              <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
                <Typography sx={{ fontWeight: 900, fontSize: 22, color: "#111827", lineHeight: 1.2 }}>
                  {student.fullName}
                </Typography>
                <Chip
                  label={student.isActive ? "Active" : "Inactive"}
                  size="small"
                  sx={{
                    height: 24,
                    fontWeight: 800,
                    fontSize: 11,
                    bgcolor: student.isActive ? "#E8F5E9" : "#FEF2F2",
                    color: student.isActive ? "#2E7D32" : "#B91C1C",
                  }}
                />
              </Stack>
              <Typography sx={{ color: "#9CA3AF", fontWeight: 600, fontSize: 13, mt: 0.25 }}>
                {student.studentId} · Member since {formatDate(student.created_at)}
              </Typography>
            </Box>
          </Stack>

          {/* Quick stats strip */}
          <Box
            sx={{
              mt: 3,
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(5, 1fr)" },
              gap: 1.5,
            }}
          >
            {[
              { label: "Total Passes", value: quickStats.passes, color: "#1565C0", bg: "#EBF2FF" },
              { label: "Event Purchases", value: quickStats.events, color: "#8B5CF6", bg: "#F5F3FF" },
              { label: "Total Spending", value: `₹${quickStats.spending.toLocaleString('en-IN')}`, color: "#EF4444", bg: "#FEF2F2" },
              { label: "Attendance Rate", value: `${quickStats.rate}%`, color: "#4CAF50", bg: "#E8F5E9" },
              { label: "Classes Enrolled", value: quickStats.classes, color: "#7C3AED", bg: "#F5F0FF" },
            ].map((s) => (
              <Box
                key={s.label}
                sx={{
                  textAlign: "center",
                  py: 1.75,
                  px: 1,
                  borderRadius: "12px",
                  bgcolor: s.bg,
                  border: `1px solid ${s.color}22`,
                }}
              >
                <Typography sx={{ fontWeight: 900, fontSize: 24, color: s.color, lineHeight: 1 }}>
                  {s.value}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "#6B7280", fontSize: 11 }}>
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Tabs ── */}
      <Box
        sx={{
          bgcolor: "#fff",
          border: "1px solid #E9EEF5",
          borderRadius: "14px",
          mb: 2.5,
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          overflow: "hidden",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : false}
          allowScrollButtonsMobile
          sx={{
            minHeight: 48,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 700,
              fontSize: 13.5,
              color: "#9CA3AF",
              minHeight: 48,
              gap: 0.75,
              whiteSpace: isMobile ? "nowrap" : "normal",
            },
            "& .Mui-selected": { color: "#111827 !important", fontWeight: 800 },
            "& .MuiTabs-indicator": {
              backgroundColor: "#a3d65c",
              height: 3,
              borderRadius: "3px 3px 0 0",
            },
          }}
        >
          {TABS.map((t, i) => {
            const counts = [
              undefined, 
              passes.length, 
              eventBookings.length,
              attendanceSummary?.totalSessions, 
              passes.length + eventBookings.length + attendanceHistory.length
            ];
            const count = counts[i];
            return (
              <Tab key={t.label} label={count !== undefined ? `${t.label} (${count})` : t.label} icon={t.icon} iconPosition="start" />
            );
          })}
        </Tabs>
      </Box>

      {activeTab === 0 && <OverviewSection student={student} />}
      {activeTab === 1 && <PassesSection passes={passes} />}
      {activeTab === 2 && <EventsSection events={eventBookings} />}
      {activeTab === 3 && <AttendanceSection data={attendanceSummary} />}
      {activeTab === 4 && (
        <ActivitySection passes={passes} events={eventBookings} attendanceHistory={attendanceHistory} />
      )}
    </Box>
  );
};

export default StudentReport;
